// Адаптеры: API (snake_case) → существующие mock-типы из src/data.ts (camelCase).
// Дают совместимость без переделки готовых UI-компонентов.

import type {
  AppOut,
  EventOut,
  GroupOut,
  KeyOut,
  LocalOut,
  UserOut,
  VersionOut,
} from "./types";
import type {
  ActiveUser,
  AppVersion,
  Application,
  DeviceGroup,
  EventColor,
  LocalServer,
  LogEvent,
} from "../data";

export function toLocalServer(l: LocalOut): LocalServer {
  return {
    id: l.id,
    location: l.location,
    address: l.address,
    status: (l.status as LocalServer["status"]) || "offline",
    clients: l.clients,
    cacheGB: l.cache_gb,
    cacheCapGB: l.cache_cap_gb,
    lastSyncMin: l.last_sync_min,
    generation: l.generation,
    certExpires: l.cert_expires,
  };
}

export function toApplication(a: AppOut): Application {
  return {
    pkg: a.pkg,
    name: a.name,
    shortDescription: a.short_description ?? "",
    version: a.current_version ?? "—",
    generation: a.current_gen ?? 0,
    developer: a.developer_login,
    status: (a.status as Application["status"]) || "draft",
    visibility: (a.visibility as Application["visibility"]) || "ALL",
    updated: a.updated_at
      ? new Date(a.updated_at).toLocaleDateString("ru-RU", { day: "2-digit", month: "2-digit", year: "2-digit" })
      : "—",
    sizeMb: a.size_mb,
    installs: a.installs,
    downloads30d: a.downloads_30d,
    iconColor: a.icon_color,
    iconLetter: a.icon_letter,
    revokedReason: a.revoked_reason ?? undefined,
    category: (a.category as Application["category"]) || "Системные",
  };
}

export function toAppVersion(v: VersionOut): AppVersion {
  return {
    version: v.version,
    generation: v.generation,
    changelog: v.changelog ?? "",
    sizeMb: v.size_mb ?? 0,
    date: v.published_at,
    status: (v.status as AppVersion["status"]) || "previous",
    hasDelta: v.has_delta,
  };
}

export function toLogEvent(e: EventOut): LogEvent {
  return {
    id: e.id,
    color: (e.color as EventColor) || "gray",
    who: e.who,
    text: e.text,
    detail: e.detail ?? "",
    when: e.when_rel,
    time: e.time_h,
    type: e.type,
    source: e.source,
    target: e.target ?? "",
  };
}

const ROLE_LABEL: Record<string, ActiveUser["role"]> = {
  admin: "глоб. админ",
  dev: "разработчик",
  local_admin: "лок. админ",
  user: "пользователь",
};

export function toActiveUser(u: UserOut): ActiveUser {
  return {
    initials: initialsOf(u.full_name),
    fullName: u.full_name,
    login: u.login,
    role: ROLE_LABEL[u.role] ?? "пользователь",
    group: u.group_code ?? "—",
    device: "—",
    online: "—",
  };
}

export function toDeviceGroup(g: GroupOut): DeviceGroup {
  return {
    code: (g.code as DeviceGroup["code"]) || "ALL",
    letters: g.letters,
    name: g.name,
    devices: g.devices,
    appsVisible: g.apps,
    description: g.description ?? "",
  };
}

export type KeyView = {
  id: string;
  name: string;
  issued: string;
  expires: string;
  status: "active" | "revoked";
  sha256: string;
  signsApps: number;
};

export function toKeyView(k: KeyOut): KeyView {
  return {
    id: k.id,
    name: k.name,
    issued: k.issued,
    expires: k.expires,
    status: (k.status as KeyView["status"]) || "revoked",
    sha256: k.sha256,
    signsApps: k.signs_apps,
  };
}

function initialsOf(fullName: string): string {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}
